<script setup lang="ts">
  import { ref } from 'vue';
  import { VForm } from 'vuetify/components/VForm';

  import type { ICreateSupportTicket } from '@/interfaces/agency/support';
  import { useSupportStore } from '@/stores/support';
  import { requiredValidator } from '@validators';

  const props = defineProps<Props>();

  const emit = defineEmits<Emit>();

  const supportStore = useSupportStore();

  const MAX_FILES = 5;
  const MAX_TOTAL_SIZE_MB = 20;
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

  interface Props {
    isDialogVisible: boolean;
  }

  interface Emit {
    (e: 'update:isDialogVisible', val: boolean): void;
    (e: 'submitted'): void;
  }

  const refVForm = ref<VForm>();
  const isSubmitting = ref(false);
  const attachments = ref<File[]>([]);
  const fileInputRef = ref<HTMLInputElement | null>(null);
  const attachmentError = ref('');

  const form = ref<ICreateSupportTicket>({
    subject: '',
    description: '',
    type: null,
    priority: null
  });

  const ticketTypes = ['Question', 'Incident', 'Problem', 'Feature Request', 'Refund'];

  const priorityOptions = [
    { title: 'Low', value: 1 },
    { title: 'Medium', value: 2 },
    { title: 'High', value: 3 },
    { title: 'Urgent', value: 4 }
  ];

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalSize = computed(() => attachments.value.reduce((sum, f) => sum + f.size, 0));

  const onFilesSelected = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files || []);

    attachmentError.value = '';

    const merged = [...attachments.value, ...selected];

    if (merged.length > MAX_FILES) {
      attachmentError.value = `You can attach up to ${MAX_FILES} files only.`;
      input.value = '';

      return;
    }

    const mergedSize = merged.reduce((sum, f) => sum + f.size, 0);
    if (mergedSize > MAX_TOTAL_SIZE_BYTES) {
      attachmentError.value = `Total file size must not exceed ${MAX_TOTAL_SIZE_MB} MB.`;
      input.value = '';

      return;
    }

    attachments.value = merged;
    input.value = '';
  };

  const removeFile = (index: number) => {
    attachments.value.splice(index, 1);
    attachmentError.value = '';
  };

  const fileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return 'tabler-photo';
    if (file.type === 'application/pdf') return 'tabler-file-type-pdf';
    if (file.type.startsWith('video/')) return 'tabler-video';
    if (file.type.startsWith('audio/')) return 'tabler-music';
    if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx'))
      return 'tabler-file-type-doc';
    if (file.type.includes('sheet') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx'))
      return 'tabler-file-type-xls';

    return 'tabler-file';
  };

  const previewUrl = ref<string | null>(null);
  const previewType = ref<'image' | 'video' | 'audio' | null>(null);
  const isPreviewVisible = ref(false);

  const previewFile = (file: File) => {
    if (file.type.startsWith('image/')) {
      previewUrl.value = URL.createObjectURL(file);
      previewType.value = 'image';
      isPreviewVisible.value = true;
    } else if (file.type.startsWith('video/')) {
      previewUrl.value = URL.createObjectURL(file);
      previewType.value = 'video';
      isPreviewVisible.value = true;
    } else if (file.type.startsWith('audio/')) {
      previewUrl.value = URL.createObjectURL(file);
      previewType.value = 'audio';
      isPreviewVisible.value = true;
    } else {
      // PDF, DOC, etc. — open in new tab
      const url = URL.createObjectURL(file);

      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  };

  const closePreview = () => {
    isPreviewVisible.value = false;
    if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
    previewType.value = null;
  };

  const closeDialog = () => {
    emit('update:isDialogVisible', false);
  };

  const onFormReset = () => {
    form.value = { subject: '', description: '', type: '', priority: 0 };
    attachments.value = [];
    attachmentError.value = '';
    refVForm.value?.reset();
    closeDialog();
  };

  const onFormSubmit = () => {
    refVForm.value?.validate().then(async ({ valid: isValid }) => {
      if (!isValid) return;

      isSubmitting.value = true;
      try {
        const formData = new FormData();

        formData.append('subject', form.value.subject);
        formData.append('description', form.value.description);
        formData.append('type', form.value.type!);
        formData.append('priority', String(form.value.priority!));
        attachments.value.forEach(file => formData.append('attachments[]', file));

        await supportStore.createTicket(formData);
        emit('submitted');
        onFormReset();
      } catch {
        /* handled globally by axios interceptor */
      } finally {
        isSubmitting.value = false;
      }
    });
  };
</script>

<template>
  <VDialog :model-value="props.isDialogVisible" persistent max-width="600" @update:model-value="closeDialog">
    <DialogCloseBtn @click="closeDialog" />

    <VCard class="py-sm-5 pt-sm-5 py-5 pt-5">
      <div class="ms-5 pb-4">
        <div class="text-h5 text-dark font-weight-bold">Contact Support</div>
        <div class="text-body-2 text-medium-emphasis mt-1">We'll get back to you within 24 hours</div>
      </div>

      <VDivider />

      <VCardText class="px-5 pt-3">
        <VForm ref="refVForm" @submit.prevent="onFormSubmit">
          <VRow>
            <VCol cols="12">
              <AppTextField
                v-model="form.subject"
                label="Subject"
                placeholder="Brief summary of your issue"
                :rules="[requiredValidator]"
                :mandatory="true"
              />
            </VCol>

            <VCol cols="12" sm="6">
              <AppSelect
                v-model="form.type"
                label="Type"
                placeholder="Select type"
                :items="ticketTypes"
                :rules="[requiredValidator]"
                :mandatory="true"
              />
            </VCol>

            <VCol cols="12" sm="6">
              <AppSelect
                v-model="form.priority"
                label="Priority"
                placeholder="Select priority"
                :items="priorityOptions"
                item-title="title"
                item-value="value"
                :rules="[requiredValidator]"
                :mandatory="true"
              />
            </VCol>

            <VCol cols="12">
              <AppTextarea
                v-model="form.description"
                label="Description"
                placeholder="Please describe your issue in detail..."
                :rules="[requiredValidator]"
                :mandatory="true"
                rows="3"
                no-resize
              />
            </VCol>

            <!-- Attachments -->
            <VCol cols="12">
              <VLabel class="mb-2 text-body-2 font-weight-medium text-high-emphasis">
                Attachments
                <span class="text-disabled ms-1 font-weight-regular">
                  (optional, max {{ MAX_FILES }} files · {{ MAX_TOTAL_SIZE_MB }} MB total)
                </span>
              </VLabel>

              <div class="attach-zone" :class="{ 'attach-zone--has-files': attachments.length }">
                <!-- Empty state -->
                <div v-if="!attachments.length" class="attach-zone__empty" @click="fileInputRef?.click()">
                  <VIcon icon="tabler-cloud-upload" size="26" color="primary" />
                  <span class="attach-zone__empty-text">Click to upload files</span>
                  <span class="attach-zone__empty-hint">PNG, JPG, PDF, DOC up to 20 MB total</span>
                </div>

                <!-- File list -->
                <div v-else class="attach-zone__files">
                  <div v-for="(file, i) in attachments" :key="i" class="attach-file-row">
                    <VIcon :icon="fileIcon(file)" size="16" color="primary" class="flex-shrink-0" />
                    <span class="attach-file-row__name">{{ file.name }}</span>
                    <span class="attach-file-row__size">{{ formatFileSize(file.size) }}</span>
                    <VBtn
                      icon
                      size="x-small"
                      variant="text"
                      color="secondary"
                      class="flex-shrink-0"
                      @click="previewFile(file)"
                    >
                      <VIcon icon="tabler-eye" size="14" />
                    </VBtn>
                    <VBtn icon size="x-small" variant="text" color="error" class="flex-shrink-0" @click="removeFile(i)">
                      <VIcon icon="tabler-x" size="14" />
                    </VBtn>
                  </div>

                  <div class="attach-zone__bar">
                    <VBtn
                      v-if="attachments.length < MAX_FILES"
                      variant="text"
                      color="primary"
                      size="small"
                      prepend-icon="tabler-plus"
                      class="pa-0"
                      @click="fileInputRef?.click()"
                    >
                      Add more
                    </VBtn>
                    <span v-else />
                    <span class="attach-zone__total">{{ formatFileSize(totalSize) }} / {{ MAX_TOTAL_SIZE_MB }} MB</span>
                  </div>
                </div>
              </div>

              <input ref="fileInputRef" type="file" multiple class="d-none" @change="onFilesSelected" />

              <div v-if="attachmentError" class="text-caption text-error mt-1">{{ attachmentError }}</div>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VDivider class="mt-2" />

      <VCardText class="mx-3 pb-0">
        <div class="d-flex flex-wrap justify-end gap-4">
          <VBtn color="default" variant="tonal" :disabled="isSubmitting" @click="onFormReset">Cancel</VBtn>
          <VBtn color="primary" :loading="isSubmitting" @click="onFormSubmit">Submit Ticket</VBtn>
        </div>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- File Preview Dialog -->
  <VDialog v-model="isPreviewVisible" max-width="800" @update:model-value="val => !val && closePreview()">
    <DialogCloseBtn @click="closePreview" />
    <VCard class="pa-4">
      <img
        v-if="previewType === 'image'"
        :src="previewUrl!"
        style="max-width: 100%; max-height: 70vh; display: block; margin: auto; border-radius: 6px"
      />
      <video
        v-else-if="previewType === 'video'"
        :src="previewUrl!"
        controls
        style="max-width: 100%; max-height: 70vh; display: block; margin: auto; border-radius: 6px"
      />
      <audio
        v-else-if="previewType === 'audio'"
        :src="previewUrl!"
        controls
        style="width: 100%; display: block; margin: auto"
      />
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
  .attach-zone {
    border: 1.5px dashed rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 8px;
    background: rgb(var(--v-theme-surface));
    transition: border-color 200ms;
    overflow: hidden;
  }

  .attach-zone--has-files {
    border-style: solid;
  }

  .attach-zone__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 20px 16px;
    cursor: pointer;
    user-select: none;
    transition: background 150ms;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.04);
    }
  }

  .attach-zone__empty-text {
    font-size: 13.5px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }

  .attach-zone__empty-hint {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
  }

  .attach-zone__files {
    padding: 6px 0;
  }

  .attach-file-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

    &:last-of-type {
      border-block-end: none;
    }
  }

  .attach-file-row__name {
    flex: 1;
    font-size: 13px;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .attach-file-row__size {
    font-size: 11px;
    color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
    white-space: nowrap;
  }

  .attach-zone__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 14px 4px;
    border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  .attach-zone__total {
    font-size: 11px;
    color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
  }
</style>
